<template>
    <div>
        <AdminPageHeader title="Dashboard" description="Ingresos, gastos y balance del periodo seleccionado.">
            <template #tabs>
                <AdmTabs :model-value="mode" :tabs="modes" label="Vista temporal" @update:model-value="setMode" />
            </template>
        </AdminPageHeader>

        <AdmCard title="Filtros" class="dash__filters">
            <div v-if="mode === 'custom'" class="dash__filter-row">
                <AdmField v-model="customRange.from" label="Desde" type="date" />
                <AdmField v-model="customRange.to" label="Hasta" type="date" />
            </div>

            <div v-if="mode === 'comparison'" class="dash__filter-row">
                <div class="dash__multi">
                    <span class="dash__multi-label">Años a comparar (máx. 5)</span>
                    <multiselect
                        v-model="selectedYears"
                        :options="availableYears"
                        :multiple="true"
                        placeholder="Selecciona años"
                        :close-on-select="false"
                        :max="5"
                    />
                </div>
                <fieldset class="dash__metric">
                    <legend>Métrica</legend>
                    <label v-for="m in metricOptions" :key="m.value" class="dash__radio">
                        <input type="radio" :value="m.value" v-model="comparisonMetric">
                        <span>{{ m.label }}</span>
                    </label>
                </fieldset>
            </div>

            <div class="dash__filter-row">
                <div class="dash__multi">
                    <span class="dash__multi-label">Categorías</span>
                    <multiselect
                        v-model="selectedCategories"
                        :options="availableCategories"
                        :multiple="true"
                        placeholder="Todas las categorías"
                        label="name"
                        track-by="id"
                        :close-on-select="false"
                    />
                </div>
                <div class="dash__multi">
                    <span class="dash__multi-label">Terceros</span>
                    <multiselect
                        v-model="selectedThirdParties"
                        :options="availableThirdParties"
                        :multiple="true"
                        placeholder="Todos los terceros"
                        label="name"
                        track-by="id"
                        :close-on-select="false"
                    />
                </div>
                <div class="dash__multi">
                    <span class="dash__multi-label">Cajas</span>
                    <multiselect
                        v-model="selectedCajas"
                        :options="availableCajas"
                        :multiple="true"
                        placeholder="Todas las cajas"
                        label="name"
                        track-by="id"
                        :close-on-select="false"
                    />
                </div>
            </div>
        </AdmCard>

        <section v-if="mode !== 'comparison'" class="dash__kpis">
            <article class="dash__kpi dash__kpi--ingresos">
                <span class="dash__kpi-label">Ingresos</span>
                <span class="dash__kpi-value">{{ formatCurrency(summary.totals.ingresos) }}</span>
            </article>
            <article class="dash__kpi dash__kpi--gastos">
                <span class="dash__kpi-label">Gastos</span>
                <span class="dash__kpi-value">{{ formatCurrency(summary.totals.gastos) }}</span>
            </article>
            <article
                class="dash__kpi dash__kpi--balance"
                :class="{ 'is-negative': summary.totals.balance < 0 }"
            >
                <span class="dash__kpi-label">Balance</span>
                <span class="dash__kpi-value">{{ formatCurrency(summary.totals.balance) }}</span>
            </article>
        </section>

        <AdmCard :title="chartTitle" class="dash__chart-card">
            <div class="dash__chart">
                <Bar v-if="chartReady" :data="chartData" :options="chartOptions" />
                <p v-else class="adm-muted">Cargando datos…</p>
            </div>
        </AdmCard>

        <div class="dash__breakdown">
            <AdmCard title="Top categorías">
                <div class="dash__block">
                    <h4 class="adm-section-label">Ingresos</h4>
                    <ul class="dash__list">
                        <li v-for="row in byCategory.ingresos" :key="'cat-i-' + row.categoryId">
                            <span>{{ row.categoryName }}</span>
                            <strong>{{ formatCurrency(row.total) }}</strong>
                        </li>
                        <li v-if="!byCategory.ingresos.length" class="dash__empty">Sin datos</li>
                    </ul>
                </div>
                <div class="dash__block">
                    <h4 class="adm-section-label">Gastos</h4>
                    <ul class="dash__list">
                        <li v-for="row in byCategory.gastos" :key="'cat-g-' + row.categoryId">
                            <span>{{ row.categoryName }}</span>
                            <strong>{{ formatCurrency(row.total) }}</strong>
                        </li>
                        <li v-if="!byCategory.gastos.length" class="dash__empty">Sin datos</li>
                    </ul>
                </div>
            </AdmCard>

            <AdmCard title="Top terceros">
                <div class="dash__block">
                    <h4 class="adm-section-label">Ingresos</h4>
                    <ul class="dash__list">
                        <li v-for="row in byThirdParty.ingresos" :key="'tp-i-' + row.thirdPartyId">
                            <span>{{ row.thirdPartyName }}</span>
                            <strong>{{ formatCurrency(row.total) }}</strong>
                        </li>
                        <li v-if="!byThirdParty.ingresos.length" class="dash__empty">Sin datos</li>
                    </ul>
                </div>
                <div class="dash__block">
                    <h4 class="adm-section-label">Gastos</h4>
                    <ul class="dash__list">
                        <li v-for="row in byThirdParty.gastos" :key="'tp-g-' + row.thirdPartyId">
                            <span>{{ row.thirdPartyName }}</span>
                            <strong>{{ formatCurrency(row.total) }}</strong>
                        </li>
                        <li v-if="!byThirdParty.gastos.length" class="dash__empty">Sin datos</li>
                    </ul>
                </div>
            </AdmCard>

            <AdmCard title="Saldos por caja">
                <ul class="dash__list">
                    <li v-for="row in byCaja.cajas" :key="'caja-' + row.cajaId" class="dash__caja">
                        <span>{{ row.cajaName }}</span>
                        <span class="dash__caja-figures">
                            <span class="dash__caja-in" title="Ingresos">{{ formatCurrency(row.ingresos) }}</span>
                            <span class="dash__caja-out" title="Gastos">{{ formatCurrency(row.gastos) }}</span>
                            <strong :class="{ 'is-negative': row.balance < 0 }" title="Balance">
                                {{ formatCurrency(row.balance) }}
                            </strong>
                        </span>
                    </li>
                    <li v-if="!byCaja.cajas.length" class="dash__empty">Sin datos</li>
                </ul>
            </AdmCard>
        </div>
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
} from 'chart.js';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

import {
    getSummary,
    getByCategory,
    getByThirdParty,
    getYearComparison,
    getByCaja,
    buildPresetFilters,
} from '@/services/dbDashboard.js';
import { getItems } from '@/services/dbIngresos.js';
import { formatCurrencyRAE } from '@/utils/numberFormat';
import { chartColor, semanticChartColors } from '@/utils/admChartColors';
import AdminPageHeader from '@/components/Admin/AdminPageHeader.vue';
import AdmCard from '@/components/Admin/ui/AdmCard.vue';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmTabs from '@/components/Admin/ui/AdmTabs.vue';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const MODE_STORAGE_KEY = 'selk.dashboard.mode';
const VALID_MODES = ['month', 'year', 'comparison', 'custom'];

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const isoDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

export default {
    name: 'DashboardPage',
    components: { Bar, Multiselect, AdminPageHeader, AdmCard, AdmField, AdmTabs },
    data() {
        const now = new Date();
        const currentYear = now.getFullYear();
        return {
            mode: 'year',
            modes: [
                { value: 'month', label: 'Mes' },
                { value: 'year', label: 'Año' },
                { value: 'comparison', label: 'Comparativa anual' },
                { value: 'custom', label: 'Personalizado' },
            ],
            metricOptions: [
                { value: 'ingresos', label: 'Ingresos' },
                { value: 'gastos', label: 'Gastos' },
                { value: 'balance', label: 'Balance' },
            ],
            customRange: {
                from: isoDate(new Date(currentYear, 0, 1)),
                to: isoDate(now),
            },
            availableYears: Array.from({ length: 5 }, (_, i) => currentYear - 4 + i),
            selectedYears: [currentYear - 1, currentYear],
            comparisonMetric: 'ingresos',
            selectedCategories: [],
            selectedThirdParties: [],
            selectedCajas: [],
            availableCategories: [],
            availableThirdParties: [],
            availableCajas: [],
            summary: {
                totals: { ingresos: 0, gastos: 0, balance: 0 },
                series: [],
            },
            comparison: {
                metric: 'ingresos',
                years: [],
                months: [],
                series: [],
            },
            byCategory: { ingresos: [], gastos: [] },
            byThirdParty: { ingresos: [], gastos: [] },
            byCaja: { cajas: [] },
            chartReady: false,
            debounceTimer: null,
        };
    },
    computed: {
        chartTitle() {
            if (this.mode === 'month') return 'Ingresos vs Gastos por día (mes actual)';
            if (this.mode === 'year') return 'Ingresos vs Gastos por mes (año actual)';
            if (this.mode === 'comparison') return `Comparativa anual — ${this.metricLabel(this.comparison.metric)}`;
            return 'Ingresos vs Gastos (rango personalizado)';
        },
        chartData() {
            if (this.mode === 'comparison') {
                const labels = MONTH_LABELS;
                const datasets = (this.comparison.series || []).map((s, idx) => ({
                    label: String(s.year),
                    data: s.data,
                    backgroundColor: chartColor(idx),
                }));
                return { labels, datasets };
            }
            const labels = this.summary.series.map(p => this.formatPeriodLabel(p.period));
            return {
                labels,
                datasets: [
                    {
                        label: 'Ingresos',
                        data: this.summary.series.map(p => p.ingresos),
                        backgroundColor: semanticChartColors().ingresos,
                    },
                    {
                        label: 'Gastos',
                        data: this.summary.series.map(p => p.gastos),
                        backgroundColor: semanticChartColors().gastos,
                    },
                ],
            };
        },
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top' },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${this.formatCurrency(ctx.parsed.y)}`,
                        },
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: (val) => this.formatCurrency(val),
                        },
                    },
                },
            };
        },
        activeFilters() {
            const base = {
                categoryIds: this.selectedCategories.map(c => c.id),
                thirdPartyIds: this.selectedThirdParties.map(t => t.id),
                cajaIds: this.selectedCajas.map(c => c.id),
            };
            if (this.mode === 'custom') {
                return { ...base, from: this.customRange.from, to: this.customRange.to, granularity: 'month' };
            }
            const preset = buildPresetFilters(this.mode);
            return { ...base, ...preset };
        },
    },
    watch: {
        customRange: { deep: true, handler() { if (this.mode === 'custom') this.scheduleFetch(); } },
        selectedCategories() { this.scheduleFetch(); },
        selectedThirdParties() { this.scheduleFetch(); },
        selectedCajas() { this.scheduleFetch(); },
        selectedYears() { if (this.mode === 'comparison') this.scheduleFetch(); },
        comparisonMetric() { if (this.mode === 'comparison') this.scheduleFetch(); },
    },
    methods: {
        formatCurrency(value) {
            return formatCurrencyRAE(value);
        },
        formatPeriodLabel(period) {
            if (!period) return '';
            if (this.mode === 'month') return period.slice(8, 10);
            if (this.mode === 'year' || this.mode === 'custom') {
                const monthIdx = parseInt(period.slice(5, 7), 10) - 1;
                return MONTH_LABELS[monthIdx] || period.slice(0, 7);
            }
            return period;
        },
        metricLabel(metric) {
            const found = this.metricOptions.find(m => m.value === metric);
            return found ? found.label : metric;
        },
        setMode(value) {
            if (!VALID_MODES.includes(value)) return;
            this.mode = value;
            try { localStorage.setItem(MODE_STORAGE_KEY, value); } catch (_) { /* ignore */ }
            this.scheduleFetch();
        },
        restoreMode() {
            try {
                const saved = localStorage.getItem(MODE_STORAGE_KEY);
                if (saved && VALID_MODES.includes(saved)) this.mode = saved;
            } catch (_) { /* ignore */ }
        },
        scheduleFetch() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.fetchAll(), 400);
        },
        async loadSelectorOptions() {
            const [{ items: cats }, { items: tps }, { items: cajas }] = await Promise.all([
                getItems('categorias', 0, 200, {}),
                getItems('thirdParties', 0, 200, {}),
                getItems('cajas', 0, 200, {}),
            ]);
            this.availableCategories = cats || [];
            this.availableThirdParties = tps || [];
            this.availableCajas = cajas || [];
        },
        async fetchAll() {
            const f = this.activeFilters;
            if (this.mode === 'comparison') {
                const compFilters = {
                    years: this.selectedYears.length ? [...this.selectedYears].sort((a, b) => a - b) : undefined,
                    metric: this.comparisonMetric,
                    categoryIds: f.categoryIds,
                    thirdPartyIds: f.thirdPartyIds,
                };
                const yrs = compFilters.years || this.availableYears.slice(-2);
                const breakdownFilters = {
                    from: `${Math.min(...yrs)}-01-01`,
                    to: `${Math.max(...yrs)}-12-31`,
                    categoryIds: f.categoryIds,
                    thirdPartyIds: f.thirdPartyIds,
                    limit: 5,
                };
                const [comparison, byCategory, byThirdParty, byCaja] = await Promise.all([
                    getYearComparison(compFilters),
                    getByCategory(breakdownFilters),
                    getByThirdParty(breakdownFilters),
                    getByCaja(breakdownFilters),
                ]);
                this.comparison = comparison;
                this.byCategory = byCategory;
                this.byThirdParty = byThirdParty;
                this.byCaja = byCaja;
            } else {
                const [summary, byCategory, byThirdParty, byCaja] = await Promise.all([
                    getSummary(f),
                    getByCategory({ ...f, limit: 5 }),
                    getByThirdParty({ ...f, limit: 5 }),
                    getByCaja(f),
                ]);
                this.summary = summary;
                this.byCategory = byCategory;
                this.byThirdParty = byThirdParty;
                this.byCaja = byCaja;
            }
            this.chartReady = true;
        },
    },
    async mounted() {
        this.restoreMode();
        await this.loadSelectorOptions();
        await this.fetchAll();
    },
};
</script>

<style scoped>
.dash__filters { margin-bottom: var(--adm-gap-lg); }

.dash__filter-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: var(--adm-gap);
    align-items: start;
}

.dash__filter-row + .dash__filter-row { margin-top: var(--adm-gap); }

.dash__multi {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    min-width: 0;
}

.dash__multi-label {
    font-family: var(--font-body);
    font-size: var(--adm-font-sm);
    font-weight: 600;
    color: var(--color-text-strong);
}

.dash__metric {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--adm-gap);
    margin: 0;
    padding: 0.4rem 0.75rem;
    border: 1px solid var(--adm-border);
    border-radius: var(--adm-radius-sm);
}

.dash__metric legend {
    padding: 0 0.3rem;
    font-size: var(--adm-font-sm);
    font-weight: 600;
    color: var(--color-text-strong);
}

.dash__radio {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: var(--adm-font-sm);
    color: var(--color-text);
}

/* --- KPIs --- */
.dash__kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--adm-gap);
    margin-bottom: var(--adm-gap-lg);
}

.dash__kpi {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: var(--adm-gap) var(--adm-gap-lg);
    border-radius: var(--adm-radius);
    color: var(--color-white);
    box-shadow: var(--adm-shadow);
}

.dash__kpi-label {
    font-family: var(--font-body);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    opacity: 0.85;
}

.dash__kpi-value {
    font-family: var(--font-subtitle);
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.2;
}

/* Los tres tonos salen de la gama azul de la marca, de claro a oscuro,
   para que se lean como una misma familia y no como tres colores sueltos. */
.dash__kpi--ingresos { background-color: var(--color-brand); }
.dash__kpi--gastos { background-color: var(--color-action); }
.dash__kpi--balance { background-color: var(--color-logo); }
.dash__kpi--balance.is-negative { background-color: var(--adm-danger-fg); }

/* --- Gráfica --- */
.dash__chart-card { margin-bottom: var(--adm-gap-lg); }

.dash__chart {
    position: relative;
    height: 360px;
}

/* --- Desgloses --- */
.dash__breakdown {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: var(--adm-gap-lg);
    align-items: start;
}

.dash__block + .dash__block { margin-top: var(--adm-gap); }

.dash__list {
    list-style: none;
    margin: 0.4rem 0 0 0;
    padding: 0;
    font-size: var(--adm-font-sm);
}

.dash__list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--adm-gap);
    padding: 0.4rem 0;
    border-bottom: 1px solid var(--adm-border);
}

.dash__list li:last-child { border-bottom: none; }

.dash__list strong { color: var(--color-title); }

.dash__empty {
    justify-content: center;
    color: var(--color-text);
}

.dash__caja-figures {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
}

.dash__caja-in { color: var(--color-brand); }
.dash__caja-out { color: var(--color-action); }
.dash__caja-figures strong.is-negative { color: var(--adm-danger-fg); }

@media (max-width: 767px) {
    .dash__chart { height: 280px; }
}
</style>
